/** Shell topbar: menu (mobile), breadcrumb, ⌘K trigger, language/theme switchers, account menu */

import { Link, useLocation, useParams } from 'react-router-dom'
import { ChevronRight, Menu, Search } from 'lucide-react'
import { useProjects } from '../../hooks/use-projects'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { LanguageSwitcher, ThemeSwitcher } from './preference-switchers'
import { UserMenu } from './user-menu'
import { WORKSPACE_NAV_ITEMS, activeNavTarget, projectNavItems, projectPath } from './nav-config'

const isMac = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.userAgent)

/** Project name › section on /p/:slug pages; the workspace page name elsewhere */
function Breadcrumb() {
  const t = useT()
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const { pathname } = useLocation()
  const { data: projects } = useProjects()

  if (!projectSlug) {
    const target = activeNavTarget(WORKSPACE_NAV_ITEMS, pathname)
    const item = WORKSPACE_NAV_ITEMS.find((i) => i.to === target)
    return item ? (
      <span className="truncate text-sm font-semibold text-fg" aria-current="page">
        {t(item.labelKey)}
      </span>
    ) : null
  }

  const items = projectNavItems(projectSlug)
  const section = items.find((i) => i.to === activeNavTarget(items, pathname))
  const name = projects?.find((p) => p.slug === projectSlug)?.name ?? projectSlug

  return (
    <>
      <Link to={projectPath(projectSlug)} className="min-w-0 truncate rounded-md text-sm font-semibold text-fg hover:text-accent-text">
        {name}
      </Link>
      {section && (
        <>
          <ChevronRight className="hidden h-4 w-4 shrink-0 text-fg-subtle sm:block" aria-hidden="true" />
          <span className="hidden truncate text-sm text-fg-muted sm:block" aria-current="page">
            {t(section.labelKey)}
          </span>
        </>
      )}
    </>
  )
}

interface ShellTopbarProps {
  onOpenMenu: () => void
}

export function ShellTopbar({ onOpenMenu }: ShellTopbarProps) {
  const t = useT()
  const setCommandPaletteOpen = useAppStore((s) => s.setCommandPaletteOpen)

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface px-4">
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label={t('shell.openMenu')}
        className="-ml-2 cursor-pointer rounded-md p-2 text-fg-muted hover:bg-surface-hover hover:text-fg md:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      <nav aria-label={t('shell.breadcrumb')} className="flex min-w-0 flex-1 items-center gap-1.5">
        <Breadcrumb />
      </nav>

      <button
        type="button"
        onClick={() => setCommandPaletteOpen(true)}
        aria-label={t('topbar.search')}
        className="flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-md border border-border bg-surface-muted px-2.5 text-sm text-fg-subtle hover:border-border-strong hover:text-fg"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
        <span className="hidden lg:inline">{t('topbar.search')}</span>
        <kbd className="hidden rounded border border-border px-1.5 text-xs font-sans text-fg-subtle sm:inline">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>

      <LanguageSwitcher className="hidden sm:inline-flex" />
      <ThemeSwitcher className="hidden sm:inline-flex" />
      <UserMenu />
    </header>
  )
}
